import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, CircleHelp, ShieldCheck, Globe, TrendingUp, Key, Zap, Users, Camera } from 'lucide-react';
import { ROSTER_DATA } from '../constants';
import { RosterItem } from '../types';
import gsap from 'gsap';

// Icon mapping helper
const getIconForId = (id: string) => {
  switch(id) {
    case '1': return <Camera size={18} className="text-primary" />; // Strategic Exposure
    case '2': return <ShieldCheck size={18} className="text-primary" />; // Professional Production
    case '3': return <Key size={18} className="text-primary" />; // Real Industry Access (Changed to Key)
    case '4': return <TrendingUp size={18} className="text-primary" />; // Brand Development
    case '5': return <Globe size={18} className="text-primary" />; // Multi-City Reach
    case '6': return <Users size={18} className="text-primary" />; // Dedicated Tour Support
    default: return <Zap size={18} className="text-primary" />;
  }
};

const RosterCard: React.FC<{ item: RosterItem }> = ({ item }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div 
      className="roster-card group relative overflow-hidden border border-slate-200 dark:border-slate-800 bg-white/50 dark:bg-slate-900/30 p-4 rounded-sm hover:border-primary/50 transition-colors flex flex-col justify-between h-full opacity-0 translate-y-8"
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-start justify-between mb-2 cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
          <div>
            <span className="block font-mono text-[9px] text-primary mb-1">
              NO. {item.deploymentId}
            </span>
            <h3 className="text-lg font-bold uppercase font-display leading-tight select-none text-slate-900 dark:text-white group-hover:text-primary transition-colors">
              {item.name}
            </h3>
          </div>
          <div className="h-9 w-9 rounded-full border border-primary/20 bg-primary/10 flex items-center justify-center flex-shrink-0 ml-2 shadow-[0_0_10px_rgba(249,115,22,0.15)] group-hover:shadow-[0_0_15px_rgba(249,115,22,0.3)] transition-shadow">
             {getIconForId(item.id)}
          </div>
        </div>
        
        {/* Collapsible Content */}
        <div 
            className={`grid transition-all duration-300 ease-in-out ${
                isOpen ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0'
            }`}
        >
            <div className="overflow-hidden">
                <p className="text-xs font-medium text-slate-600 dark:text-slate-300 font-mono leading-relaxed pb-1">
                  {item.supporting}
                </p>
            </div>
        </div>

        {/* Arrow Trigger */}
        <div 
            className="mt-auto flex items-center justify-end pt-1 cursor-pointer"
            onClick={(e) => {
                e.stopPropagation();
                setIsOpen(!isOpen);
            }}
        >
            {!isOpen && (
                <span className="text-[9px] font-mono font-bold text-slate-400 group-hover:text-primary uppercase tracking-widest mr-2 transition-colors">
                    Read More
                </span>
            )}
            <button 
                className="p-1 -mr-1 text-slate-400 group-hover:text-primary transition-colors focus:outline-none"
                aria-label={isOpen ? "Collapse" : "Expand"}
            >
                <ChevronDown 
                    className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
                    size={16} 
                />
            </button>
        </div>
      </div>
    </div>
  );
};

const Roster: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    gsap.to('.roster-card', {
                        y: 0,
                        opacity: 1,
                        duration: 0.6,
                        stagger: 0.1,
                        ease: 'power2.out',
                        overwrite: true
                    });
                    observer.disconnect();
                }
            });
        },
        { threshold: 0.1 }
    );
    
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <section className="px-6 mb-16" ref={containerRef}>
      <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-8 gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tighter uppercase text-slate-900 dark:text-white font-display">
            Why Tour With Us?
          </h2>
        </div>
        <div className="text-right self-end sm:self-auto">
          <CircleHelp className="text-primary animate-pulse" size={32} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ROSTER_DATA.map((artist) => (
          <RosterCard key={artist.id} item={artist} />
        ))}
      </div>
    </section>
  );
};

export default Roster;